import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Wifi } from "lucide-react";
import axios from "axios";
import { API } from "../App";

const VERSION = "4.0";

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const [siteSettings, setSiteSettings] = useState(null);

  useEffect(() => {
    axios.get(`${API}/site-settings`).then(res => setSiteSettings(res.data)).catch(() => {});
  }, []);

  const socials = siteSettings || {};

  return (
    <footer className="bg-[#15161C] border-t border-[#2A2B32]" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
          {/* Company Info */}
          <div className="lg:col-span-2 space-y-4">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 rounded-full overflow-hidden bg-gradient-to-br from-[#4A2B6E] via-[#0D5C63] to-[#FF6B35] p-0.5">
                <div className="w-full h-full rounded-full bg-[#0B0C10] flex items-center justify-center">
                  <span className="text-xl font-bold gradient-text">NL</span>
                </div>
              </div>
              <div>
                <p className="text-xl font-bold text-white">New Leaf PMC</p>
                <p className="text-[#A0AAB2] text-sm">Handyman & Renovation</p>
              </div>
            </div>
            <p className="text-[#A0AAB2] text-sm leading-relaxed max-w-md">
              Family-owned and operated in Central Florida since 2019. We specialize in interior & exterior renovations, from drywall to complete home makeovers.
            </p>
            <div className="space-y-2">
              <a href="tel:863-340-9244" className="flex items-center space-x-2 text-[#A0AAB2] hover:text-[#FF6B35] transition-colors" data-testid="footer-phone">
                <Phone size={16} />
                <span className="text-sm">863-340-9244</span>
              </a>
              <a href="tel:863-340-8262" className="flex items-center space-x-2 text-[#A0AAB2] hover:text-[#FF6B35] transition-colors">
                <Phone size={16} />
                <span className="text-sm">863-340-8262</span>
              </a>
              <a href="mailto:newleafpmcllc@gmail.com" className="flex items-center space-x-2 text-[#A0AAB2] hover:text-[#FF6B35] transition-colors" data-testid="footer-email">
                <Mail size={16} />
                <span className="text-sm">newleafpmcllc@gmail.com</span>
              </a>
              <div className="flex items-center space-x-2 text-[#A0AAB2]">
                <MapPin size={16} />
                <span className="text-sm">Central Florida</span>
              </div>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-white font-semibold text-lg mb-4">Quick Links</h3>
            <ul className="space-y-3">
              {[
                { to: "/services", label: "Our Services" },
                { to: "/booking", label: "Book Estimate" },
                { to: "/gallery", label: "Gallery" },
                { to: "/contact", label: "Contact Us" },
              ].map(link => (
                <li key={link.to}>
                  <Link to={link.to} className="text-[#A0AAB2] hover:text-[#FF6B35] text-sm transition-colors">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Social */}
          <div>
            <h3 className="text-white font-semibold text-lg mb-4">Follow Us</h3>
            <div className="flex space-x-3">
              {socials.instagram_url && (
                <a href={socials.instagram_url} target="_blank" rel="noopener noreferrer"
                  className="w-10 h-10 rounded-lg bg-[#1F2027] border border-[#2A2B32] flex items-center justify-center text-[#A0AAB2] hover:text-[#FF6B35] hover:border-[#FF6B35] transition-all"
                  data-testid="social-instagram">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
                </a>
              )}
              {socials.facebook_url && (
                <a href={socials.facebook_url} target="_blank" rel="noopener noreferrer"
                  className="w-10 h-10 rounded-lg bg-[#1F2027] border border-[#2A2B32] flex items-center justify-center text-[#A0AAB2] hover:text-[#FF6B35] hover:border-[#FF6B35] transition-all"
                  data-testid="social-facebook">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                </a>
              )}
              {socials.tiktok_url && (
                <a href={socials.tiktok_url} target="_blank" rel="noopener noreferrer"
                  className="w-10 h-10 rounded-lg bg-[#1F2027] border border-[#2A2B32] flex items-center justify-center text-[#A0AAB2] hover:text-[#FF6B35] hover:border-[#FF6B35] transition-all"
                  data-testid="social-tiktok">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/></svg>
                </a>
              )}
              {!socials.instagram_url && !socials.facebook_url && !socials.tiktok_url && (
                <p className="text-[#A0AAB2] text-xs">Social links coming soon</p>
              )}
            </div>
            {socials.display_username && (
              <p className="text-[#A0AAB2] text-xs mt-3">{socials.display_username}</p>
            )}
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-12 pt-8 border-t border-[#2A2B32]">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <div className="text-center md:text-left">
              <p className="text-[#A0AAB2] text-sm">
                &copy; {currentYear} New Leaf PMC LLC. All rights reserved.
              </p>
              <p className="text-[#A0AAB2]/60 text-xs mt-1">
                863-340-9244 | newleafpmcllc@gmail.com | Central Florida
              </p>
            </div>
            <div className="flex items-center space-x-6">
              <Link to="/services" className="text-[#A0AAB2] hover:text-[#FF6B35] text-sm transition-colors">Services</Link>
              <Link to="/booking" className="text-[#A0AAB2] hover:text-[#FF6B35] text-sm transition-colors">Book Now</Link>
              <Link to="/contact" className="text-[#A0AAB2] hover:text-[#FF6B35] text-sm transition-colors">Contact</Link>
              <span className="text-[#2A2B32]">|</span>
              <div className="flex items-center space-x-1.5">
                <Wifi size={14} className="text-[#22C55E]" />
                <span className="text-[#22C55E] text-sm font-medium">v{VERSION}</span>
              </div>
            </div>
          </div>
          <div className="mt-4 text-center">
            <p className="text-[#A0AAB2]/40 text-xs" data-testid="footer-branding">
              Built & Powered by Prestigious Marketing
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
